// Enhanced API service with JWT authentication
import { NewsItem, Event, PriestReflection, TeamMember, Ministry } from '../types';
import { authService } from './auth';

interface GalleryImage {
  key: string;
  fileName: string;
  url: string;
  size: number;
  lastModified: string;
  caption?: string;
}

// Check if we're in production or have AWS config
const USE_AWS = !!(window.location.hostname !== 'localhost' || process.env.REACT_APP_AWS_USER_POOL_ID);

// API base URL
const API_BASE_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:3001'
  : 'https://tag8l7ywck.execute-api.eu-north-1.amazonaws.com/prod';

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url: string, options: RequestInit = {}) => {
  const token = await authService.getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
    ...options.headers
  };
  
  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers
  });
  
  if (!response.ok) {
    let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.error) {
        errorMessage += ` - ${errorData.error}`;
      }
      if (errorData.details) {
        errorMessage += ` (${errorData.details})`;
      }
    } catch (e) {
      // If we can't parse the error response, use the original message
    }
    throw new Error(errorMessage);
  }
  
  return response.json();
};

// Empty data store
let mockData = {
  events: [] as Event[],
  news: [] as NewsItem[],
  reflections: [] as PriestReflection[],
  team: [] as TeamMember[],
  history: [] as any[]
};

export const apiService = {
  // Events
  async getEvents(): Promise<Event[]> {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/events');
      } catch (error) {
        console.warn('API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createEvent(event: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/events', {
          method: 'POST',
          body: JSON.stringify(event)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const newEvent = { ...event, id: Date.now().toString() };
    mockData.events.push(newEvent);
    return newEvent;
  },

  async updateEvent(id: string, event: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/events/${id}`, {
          method: 'PUT',
          body: JSON.stringify(event)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
        return null;
      }
    }
    const index = mockData.events.findIndex(e => e.id === id);
    if (index !== -1) {
      mockData.events[index] = { ...mockData.events[index], ...event };
      return mockData.events[index];
    }
    return null;
  },

  async deleteEvent(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/events/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
        return false;
      }
    }
    mockData.events = mockData.events.filter(e => e.id !== id);
    return true;
  },

  // News
  async getNews(): Promise<NewsItem[]> {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/news');
      } catch (error) {
        console.warn('API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createNews(news: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/news', {
          method: 'POST',
          body: JSON.stringify(news)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const newNews = { ...news, id: Date.now().toString() };
    mockData.news.push(newNews);
    return newNews;
  },

  async updateNews(id: string, news: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/news/${id}`, {
          method: 'PUT',
          body: JSON.stringify(news)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
        return null;
      }
    }
    const index = mockData.news.findIndex(n => n.id === id);
    if (index !== -1) {
      mockData.news[index] = { ...mockData.news[index], ...news };
      return mockData.news[index];
    }
    return null;
  },

  async deleteNews(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/news/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
        return false;
      }
    }
    mockData.news = mockData.news.filter(n => n.id !== id);
    return true;
  },

  // Reflections
  async getReflections(): Promise<PriestReflection[]> {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/reflections');
      } catch (error) {
        console.warn('API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createReflection(reflection: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/reflections', {
          method: 'POST',
          body: JSON.stringify(reflection)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const newReflection = { ...reflection, id: Date.now().toString() };
    mockData.reflections.push(newReflection);
    return newReflection;
  },

  async updateReflection(id: string, reflection: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/reflections/${id}`, {
          method: 'PUT',
          body: JSON.stringify(reflection)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const index = mockData.reflections.findIndex(r => r.id === id);
    if (index !== -1) {
      mockData.reflections[index] = { ...mockData.reflections[index], ...reflection };
    }
    return mockData.reflections[index];
  },

  async deleteReflection(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/reflections/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    mockData.reflections = mockData.reflections.filter(r => r.id !== id);
    return true;
  },

  // Team
  async getTeam(): Promise<TeamMember[]> {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/team');
      } catch (error) {
        console.warn('API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createTeamMember(member: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/team', {
          method: 'POST',
          body: JSON.stringify(member)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const newMember = { ...member, id: Date.now().toString() };
    mockData.team.push(newMember);
    return newMember;
  },

  async updateTeamMember(id: string, member: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/team/${id}`, {
          method: 'PUT',
          body: JSON.stringify(member)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const index = mockData.team.findIndex(m => m.id === id);
    if (index !== -1) {
      mockData.team[index] = { ...mockData.team[index], ...member };
    }
    return mockData.team[index];
  },

  async deleteTeamMember(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/team/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    mockData.team = mockData.team.filter(m => m.id !== id);
    return true;
  },

  async uploadTeamPhoto(file: File) {
    if (USE_AWS) {
      try {
        return await this.uploadFile(file, 'team-photos');
      } catch (error) {
        console.error('Photo upload failed:', error);
        throw error;
      }
    }
    throw new Error('Photo upload not available in demo mode');
  },

  // Ministries
  async getMinistries(): Promise<Ministry[]> {
    if (USE_AWS) {
      try {
        const data = await makeAuthenticatedRequest('/api/ministries');
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.warn('Ministries API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createMinistry(ministry: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/ministries', {
          method: 'POST',
          body: JSON.stringify(ministry)
        });
      } catch (error) {
        console.error('Ministry creation failed:', error);
        throw error;
      }
    }
    throw new Error('Ministry creation not available in demo mode');
  },

  async updateMinistry(id: string, ministry: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/ministries/${id}`, {
          method: 'PUT',
          body: JSON.stringify(ministry)
        });
      } catch (error) {
        console.error('Ministry update failed:', error);
        throw error;
      }
    }
    throw new Error('Ministry update not available in demo mode');
  },

  async deleteMinistry(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/ministries/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.error('Ministry deletion failed:', error);
        throw error;
      }
    }
    throw new Error('Ministry deletion not available in demo mode');
  },

  // History
  async getHistory(): Promise<any[]> {
    if (USE_AWS) {
      try {
        const data = await makeAuthenticatedRequest('/api/history');
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.warn('API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  // Mass Times
  async getMassTimes(queryParams = ''): Promise<any[]> {
    if (USE_AWS) {
      try {
        const data = await makeAuthenticatedRequest(`/api/mass-times${queryParams}`);
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.warn('Mass times API unavailable, using fallback:', error);
        return [
          { id: 'sunday', day: 'Sunday', times: '10:30 AM', special: 'My Parish My Mission during 10:30 AM Mass', order: 1 },
          { id: 'weekdays', day: 'Weekdays (except Monday)', times: '10:00 AM', order: 2 },
          { id: 'holydays', day: 'Holy Days of Obligation', times: '10:00 AM', special: 'See newsletter for details', order: 3 }
        ];
      }
    }
    return [
      { id: 'sunday', day: 'Sunday', times: '10:30 AM', special: 'My Parish My Mission during 10:30 AM Mass', order: 1 },
      { id: 'weekdays', day: 'Weekdays (except Monday)', times: '10:00 AM', order: 2 },
      { id: 'holydays', day: 'Holy Days of Obligation', times: '10:00 AM', special: 'See newsletter for details', order: 3 }
    ];
  },

  async updateMassTime(id: string, massTime: any) {
    if (USE_AWS) {
      try {
        console.log('Updating mass time:', id, massTime);
        const result = await makeAuthenticatedRequest(`/api/mass-times/${id}`, {
          method: 'PUT',
          body: JSON.stringify(massTime)
        });
        console.log('Mass time updated successfully:', result);
        return result;
      } catch (error) {
        console.error('Mass times update failed:', error);
        throw error;
      }
    }
    throw new Error('Mass times editing not available in demo mode');
  },

  async createMassTime(massTime: any) {
    if (USE_AWS) {
      try {
        console.log('Creating mass time:', massTime);
        const result = await makeAuthenticatedRequest('/api/mass-times', {
          method: 'POST',
          body: JSON.stringify(massTime)
        });
        console.log('Mass time created successfully:', result);
        return result;
      } catch (error) {
        console.error('Mass times creation failed:', error);
        throw error;
      }
    }
    throw new Error('Mass times creation not available in demo mode');
  },

  async deleteMassTime(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/mass-times/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('Mass times API unavailable:', error);
        throw error;
      }
    }
    throw new Error('Mass times deletion not available in demo mode');
  },

  async initializeMassTimes() {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/mass-times/initialize', {
          method: 'POST',
          body: JSON.stringify({})
        });
      } catch (error) {
        console.warn('Mass times initialization failed:', error);
        throw error;
      }
    }
    throw new Error('Mass times initialization not available in demo mode');
  },

  // Devotions
  async getDevotions(): Promise<any[]> {
    if (USE_AWS) {
      try {
        const data = await makeAuthenticatedRequest('/api/devotions');
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.warn('Devotions API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createDevotion(devotion: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/devotions', {
          method: 'POST',
          body: JSON.stringify(devotion)
        });
      } catch (error) {
        console.error('Devotion creation failed:', error);
        throw error;
      }
    }
    throw new Error('Devotion creation not available in demo mode');
  },

  async updateDevotion(id: string, devotion: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/devotions/${id}`, {
          method: 'PUT',
          body: JSON.stringify(devotion)
        });
      } catch (error) {
        console.error('Devotion update failed:', error);
        throw error;
      }
    }
    throw new Error('Devotion update not available in demo mode');
  },

  async deleteDevotion(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/devotions/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('Devotions API unavailable:', error);
        throw error;
      }
    }
    throw new Error('Devotion deletion not available in demo mode');
  },

  async createHistoryEntry(history: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/history', {
          method: 'POST',
          body: JSON.stringify(history)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const newHistory = { ...history, id: Date.now().toString() };
    mockData.history.push(newHistory);
    return newHistory;
  },

  async updateHistoryEntry(id: string, history: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/history/${id}`, {
          method: 'PUT',
          body: JSON.stringify(history)
        });
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    const index = mockData.history.findIndex(h => h.id === id);
    if (index !== -1) {
      mockData.history[index] = { ...mockData.history[index], ...history };
    }
    return mockData.history[index];
  },

  async deleteHistoryEntry(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/history/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
      }
    }
    mockData.history = mockData.history.filter(h => h.id !== id);
    return true;
  },

  // Newsletters
  async uploadNewsletter(file: File, title: string, description: string, date: string, expiryDate?: string) {
    if (USE_AWS) {
      try {
        // Upload file to S3
        const fileUrl = await this.uploadFile(file, 'newsletters', expiryDate);

        // Create newsletter record
        const newsletter: Record<string, any> = {
          title,
          description,
          date,
          fileUrl,
          fileName: file.name,
          fileSize: file.size,
          uploadDate: new Date().toISOString()
        };

        if (expiryDate) {
          newsletter.expiryDate = expiryDate;
        }

        return await makeAuthenticatedRequest('/api/newsletters', {
          method: 'POST',
          body: JSON.stringify(newsletter)
        });
      } catch (error) {
        console.error('Newsletter upload failed:', error);
        throw error;
      }
    }
    throw new Error('Newsletter upload not available in demo mode');
  },

  async getNewsletters() {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/newsletters');
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
        return [];
      }
    }
    return [];
  },

  async deleteNewsletter(key: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/newsletters/${encodeURIComponent(key)}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable:', error);
        throw error;
      }
    }
    throw new Error('Newsletter deletion not available in demo mode');
  },

  // Meeting Notes
  async uploadMeetingNote(file: File, title: string, description: string, date: string, expiryDate?: string) {
    if (USE_AWS) {
      try {
        // Upload file to S3
        const fileUrl = await this.uploadFile(file, 'meetings', expiryDate);

        // Create meeting note record
        const meetingNote: Record<string, any> = {
          title,
          description,
          date,
          fileUrl,
          fileName: file.name,
          fileSize: file.size,
          uploadDate: new Date().toISOString()
        };

        if (expiryDate) {
          meetingNote.expiryDate = expiryDate;
        }

        return await makeAuthenticatedRequest('/api/meeting-notes', {
          method: 'POST',
          body: JSON.stringify(meetingNote)
        });
      } catch (error) {
        console.error('Meeting note upload failed:', error);
        throw error;
      }
    }
    throw new Error('Meeting note upload not available in demo mode');
  },

  async getMeetingNotes() {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/meeting-notes');
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
        return [];
      }
    }
    return [];
  },

  async deleteMeetingNote(key: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/meeting-notes/${encodeURIComponent(key)}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable:', error);
        throw error;
      }
    }
    throw new Error('Meeting note deletion not available in demo mode');
  },

  // Lectio Divina
  async uploadLectioDivina(file: File, title: string, description: string, date: string, expiryDate?: string) {
    if (USE_AWS) {
      try {
        // Upload file to S3
        const fileUrl = await this.uploadFile(file, 'lectio-divina', expiryDate);

        // Create lectio divina record
        const lectioDivina: Record<string, any> = {
          title,
          description,
          date,
          fileUrl,
          fileName: file.name,
          fileSize: file.size,
          uploadDate: new Date().toISOString()
        };

        if (expiryDate) {
          lectioDivina.expiryDate = expiryDate;
        }

        return await makeAuthenticatedRequest('/api/lectio-divina', {
          method: 'POST',
          body: JSON.stringify(lectioDivina)
        });
      } catch (error) {
        console.error('Lectio Divina upload failed:', error);
        throw error;
      }
    }
    throw new Error('Lectio Divina upload not available in demo mode');
  },

  async getLectioDivina() {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/lectio-divina');
      } catch (error) {
        console.warn('API unavailable, using mock data:', error);
        return [];
      }
    }
    return [];
  },

  async deleteLectioDivina(key: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/lectio-divina/${encodeURIComponent(key)}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.warn('API unavailable:', error);
        throw error;
      }
    }
    throw new Error('Lectio Divina deletion not available in demo mode');
  },

  // RCIA
  async getRcia(): Promise<any[]> {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/rcia');
      } catch (error) {
        console.warn('RCIA API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createRciaSession(session: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/rcia', {
          method: 'POST',
          body: JSON.stringify(session)
        });
      } catch (error) {
        console.error('RCIA session creation failed:', error);
        throw error;
      }
    }
    throw new Error('RCIA creation not available in demo mode');
  },

  async updateRciaSession(id: string, session: any) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/rcia/${id}`, {
          method: 'PUT',
          body: JSON.stringify(session)
        });
      } catch (error) {
        console.error('RCIA session update failed:', error);
        throw error;
      }
    }
    throw new Error('RCIA update not available in demo mode');
  },

  async deleteRciaSession(id: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/rcia/${id}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.error('RCIA session deletion failed:', error);
        throw error;
      }
    }
    throw new Error('RCIA deletion not available in demo mode');
  },

  // File uploads with presigned URLs
  async getPresignedUrl(fileName: string, fileType: string, folder: string = 'uploads', expiryDate?: string) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/uploads/presigned-url', {
          method: 'POST',
          body: JSON.stringify({ fileName, fileType, folder, expiryDate: expiryDate || undefined })
        });
      } catch (error) {
        console.error('Failed to get presigned URL:', error);
        throw error;
      }
    }
    throw new Error('File upload not available in demo mode');
  },

  async uploadFileToS3(presignedUrl: string, file: File, metadata?: Record<string, string> | null) {
    try {
      const headers: Record<string, string> = { 'Content-Type': file.type };
      if (metadata) {
        Object.assign(headers, metadata);
      }

      const response = await fetch(presignedUrl, {
        method: 'PUT',
        body: file,
        headers
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      return true;
    } catch (error) {
      console.error('S3 upload failed:', error);
      throw error;
    }
  },

  async uploadFile(file: File, folder: string = 'uploads', expiryDate?: string) {
    try {
      // Get presigned URL
      const { uploadURL, fileUrl, metadata } = await this.getPresignedUrl(file.name, file.type, folder, expiryDate);

      // Upload to S3 with metadata headers
      await this.uploadFileToS3(uploadURL, file, metadata);

      return fileUrl;
    } catch (error) {
      console.error('File upload failed:', error);
      throw error;
    }
  },

  // Gallery management
  async getGalleryFolders(): Promise<string[]> {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/gallery/folders');
      } catch (error) {
        console.warn('Gallery folders API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async getGalleryImages(folder?: string): Promise<GalleryImage[]> {
    if (USE_AWS) {
      try {
        const endpoint = folder ? `/api/gallery/images/${encodeURIComponent(folder)}` : '/api/gallery/images';
        return await makeAuthenticatedRequest(endpoint);
      } catch (error) {
        console.warn('Gallery images API unavailable:', error);
        return [];
      }
    }
    return [];
  },

  async createGalleryFolder(folderName: string) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest('/api/gallery/folder', {
          method: 'POST',
          body: JSON.stringify({ folderName })
        });
      } catch (error) {
        console.error('Gallery folder creation failed:', error);
        throw error;
      }
    }
    throw new Error('Gallery folder creation not available in demo mode');
  },

  async uploadGalleryImage(file: File, folder?: string, caption?: string, expiryDate?: string) {
    if (USE_AWS) {
      try {
        // Get presigned URL for gallery upload
        const { uploadURL, fileUrl, key, metadata } = await makeAuthenticatedRequest('/api/gallery/upload', {
          method: 'POST',
          body: JSON.stringify({
            fileName: file.name,
            fileType: file.type,
            folder: folder || '',
            expiryDate: expiryDate || undefined
          })
        });

        // Upload to S3 with metadata headers
        await this.uploadFileToS3(uploadURL, file, metadata);

        // Save caption and/or expiry metadata
        if ((caption && caption.trim()) || expiryDate) {
          await this.updateImageCaption(key, caption || '', expiryDate);
        }

        return { fileUrl, key };
      } catch (error) {
        console.error('Gallery image upload failed:', error);
        throw error;
      }
    }
    throw new Error('Gallery upload not available in demo mode');
  },

  async updateImageCaption(imageKey: string, caption: string, expiryDate?: string) {
    if (USE_AWS) {
      try {
        return await makeAuthenticatedRequest(`/api/gallery/caption/${encodeURIComponent(imageKey)}`, {
          method: 'PUT',
          body: JSON.stringify({ caption, expiryDate: expiryDate || undefined })
        });
      } catch (error) {
        console.error('Image caption update failed:', error);
        throw error;
      }
    }
    throw new Error('Caption update not available in demo mode');
  },

  async deleteGalleryImage(imageKey: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/gallery/image/${encodeURIComponent(imageKey)}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.error('Gallery image deletion failed:', error);
        throw error;
      }
    }
    throw new Error('Gallery image deletion not available in demo mode');
  },

  async deleteGalleryFolder(folderName: string) {
    if (USE_AWS) {
      try {
        await makeAuthenticatedRequest(`/api/gallery/folder/${encodeURIComponent(folderName)}`, {
          method: 'DELETE'
        });
        return true;
      } catch (error) {
        console.error('Gallery folder deletion failed:', error);
        throw error;
      }
    }
    throw new Error('Gallery folder deletion not available in demo mode');
  }
};